import { useState } from "react";
import { CalculatorPage } from "../../components/CalculatorPage";
import { ResultExplainer } from "../../components/ResultExplainer";
import { ComparisonCard } from "../../components/ComparisonCard";
import { taxAPI, formatCurrency } from "../../lib/api";
import { buildCalculatorSchema } from "../../hooks/useSEO";
import { ComparisonChart } from "../../components/ComparisonChart";
import { EmailGatePdfButton } from "../../components/EmailGatePdfButton";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";

const SALTDeductionCalculator = () => {
  const [stateTax, setStateTax] = useState("12000");
  const [propertyTax, setPropertyTax] = useState("8000");
  const [rate, setRate] = useState("24");
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);

  const run = async () => {
    setLoading(true);
    try {
      const res = await taxAPI.salt({
        state_income_tax: Number(stateTax),
        property_tax: Number(propertyTax),
        marginal_rate: Number(rate),
      });
      setResult(res);
    } catch (e) {
      toast.error("Calculation failed.");
    } finally {
      setLoading(false);
    }
  };

  const inputPanel = (
    <div className="space-y-5">
      <div>
        <label className="font-body text-sm font-semibold block mb-2">State & Local Income Tax Paid</label>
        <input
          type="number"
          inputMode="decimal"
          value={stateTax}
          onChange={(e) => setStateTax(e.target.value)}
          className="w-full px-4 py-3 font-mono text-lg border border-foreground/40 focus:border-primary focus:border-2 focus:outline-none bg-background"
          data-testid="input-state-tax"
        />
      </div>
      <div>
        <label className="font-body text-sm font-semibold block mb-2">Property Tax Paid</label>
        <input
          type="number"
          inputMode="decimal"
          value={propertyTax}
          onChange={(e) => setPropertyTax(e.target.value)}
          className="w-full px-4 py-3 font-mono text-lg border border-foreground/40 focus:border-primary focus:border-2 focus:outline-none bg-background"
          data-testid="input-property-tax"
        />
      </div>
      <div>
        <label className="font-body text-sm font-semibold block mb-2">Your Marginal Federal Rate (%)</label>
        <input
          type="number"
          inputMode="decimal"
          value={rate}
          onChange={(e) => setRate(e.target.value)}
          className="w-full px-4 py-3 font-mono text-lg border border-foreground/40 focus:border-primary focus:border-2 focus:outline-none bg-background"
          data-testid="input-marginal-rate"
        />
      </div>
      <button
        onClick={run}
        disabled={loading}
        data-testid="calculate-btn"
        className="w-full bg-primary text-primary-foreground font-mono uppercase tracking-widest text-sm px-6 py-4 mt-3 hover:brightness-110 transition flex items-center justify-center gap-2 disabled:opacity-50"
      >
        {loading ? <><Loader2 className="w-4 h-4 animate-spin" /> Calculating…</> : "Calculate SALT Savings"}
      </button>
    </div>
  );

  const resultPanel = result ? (
    <div className="space-y-6" data-testid="salt-result">
      <div className="border-2 border-foreground bg-foreground text-background p-6">
        <span className="font-mono text-[10px] uppercase tracking-[0.3em] text-background/60 mb-2 block">Bottom Line</span>
        <h3 className="font-heading text-2xl md:text-3xl font-bold leading-tight">
          SALT cap repeal saves you an additional <span className="text-primary">{formatCurrency(result.additional_benefit_2026)}</span> in 2026.
        </h3>
      </div>
      <ComparisonCard
        testid="salt-comparison"
        rows={[
          { label: "Total SALT Paid", value2025: result.inputs.total_salt_paid, value2026: result.inputs.total_salt_paid, format: "currency", hideDelta: true },
          { label: "SALT Cap", value2025: result.year_2025.cap, value2026: 999999999, format: "currency", hideDelta: true, hint: "2026: no cap" },
          { label: "Deductible SALT", value2025: result.year_2025.deductible, value2026: result.year_2026.deductible, format: "currency" },
          { label: "Federal Tax Savings", value2025: result.year_2025.tax_savings, value2026: result.year_2026.tax_savings, format: "currency" },
        ]}
      />
      <div className="bg-muted border border-foreground/15 p-5">
        <span className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground mb-1 block">Caveat</span>
        <p className="font-body text-sm">{result.year_2026.note}</p>
      </div>

      <ComparisonChart
        testid="salt-chart"
        title="SALT deductibility: 2025 vs 2026"
        rows={[
          { label: "Deductible", v2025: result.year_2025.deductible, v2026: result.year_2026.deductible },
          { label: "Tax Savings", v2025: result.year_2025.tax_savings, v2026: result.year_2026.tax_savings },
        ]}
      />

      <EmailGatePdfButton
        testid="salt-pdf"
        filename="taxshift-2026-salt.pdf"
        pdfPayload={{
          title: "2026 SALT Deduction Analysis",
          subtitle: "State & local tax cap repeal impact",
          inputs: [
            { label: "State Income Tax", value: formatCurrency(result.inputs.state_income_tax) },
            { label: "Property Tax", value: formatCurrency(result.inputs.property_tax) },
            { label: "Total SALT Paid", value: formatCurrency(result.inputs.total_salt_paid) },
            { label: "Marginal Rate", v2025: `${result.inputs.marginal_rate}%`, value: `${result.inputs.marginal_rate}%` },
          ],
          headline: `SALT cap repeal saves you an additional ${formatCurrency(result.additional_benefit_2026)} in 2026.`,
          rows: [
            { label: "SALT Cap", v2025: formatCurrency(result.year_2025.cap), v2026: "No cap" },
            { label: "Deductible SALT", v2025: formatCurrency(result.year_2025.deductible), v2026: formatCurrency(result.year_2026.deductible) },
            { label: "Federal Tax Savings", v2025: formatCurrency(result.year_2025.tax_savings), v2026: formatCurrency(result.year_2026.tax_savings) },
          ],
        }}
      />
      <ResultExplainer slug="salt-deduction-2026" />
    </div>
  ) : (
    <div className="border-2 border-dashed border-foreground/30 p-12 text-center bg-card/50" data-testid="salt-empty">
      <p className="font-heading text-xl">Enter your SALT figures and click Calculate.</p>
    </div>
  );

  return (
    <CalculatorPage
      title="2026 SALT Deduction Calculator (OBBBA $40k Cap)"
      slug="salt-deduction-2026"
      overline="Calculator · Deductions · SALT"
      description="OBBBA raised the SALT cap from $10,000 to $40,000 for 2025, with 1% annual increases through 2029. A phase-out above $500k MAGI reduces the benefit for high earners. Calculate your exact savings."
      seoTitle="2026 SALT Cap Calculator — OBBBA $40,000"
      seoKeywords="salt cap 2026, 40000 salt deduction, obbba salt cap phaseout, state and local tax deduction"
      jsonLd={buildCalculatorSchema({
        name: "2026 SALT Deduction Calculator",
        description: "Free calculator showing SALT cap repeal impact in 2026.",
        url: "/calculator/salt-deduction-2026",
      })}
      inputPanel={inputPanel}
      resultPanel={resultPanel}
      education={
        <>
          <h2 className="font-heading text-3xl md:text-4xl font-bold tracking-tight mb-6">
            The $10k cap became $40k under OBBBA
          </h2>
          <div className="font-body text-base leading-relaxed space-y-4 text-foreground/85">
            <p>
              The $10,000 SALT cap from the TCJA became one of the most politically contested
              provisions of modern tax law. OBBBA raised it to <strong>$40,000</strong> for 2025,
              with 1% annual increases through 2029, then reverts to $10,000 in 2030.
            </p>
            <p>
              A phase-out applies above $500,000 MAGI — roughly 30% of MAGI excess above that
              threshold reduces your cap, with a floor of $10,000. So even at $1M MAGI, your SALT
              cap won't fall below the original TCJA level.
            </p>
            <p className="italic">
              Caveat: the Alternative Minimum Tax returns under pre-TCJA rules for 2026, which can
              recapture a portion of high-SALT benefits for some filers. Our calculator provides a
              pre-AMT estimate.
            </p>
          </div>
        </>
      }
      relatedSlugs={[
        { slug: "tax-bracket-2026", label: "Tax Bracket" },
        { slug: "standard-deduction-2026", label: "Standard Deduction" },
      ]}
    />
  );
};

export default SALTDeductionCalculator;
